import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import { ModalBrand } from "@components/modules/brands/ModalBrand";
import {
  BrandLogo,
  Card,
  CardHeader,
  ExpandableItem,
  type ExpandableItemProps,
  LabeledItem,
  Link,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { useDeleteBrandMutation } from "@src/__generated__/graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent, useContext, useMemo } from "react";
import type { BrandModel } from "./models";
import { PageClientDetailCtx } from "./PageClientDetail";

const SectionBrand: FunctionComponent<
  React.PropsWithChildren<{
    brand: BrandModel;
  }>
> = observer(function SectionBrand({ brand }) {
  const store = useContext(PageClientDetailCtx);

  const editModal = useDisclosure();
  const onEditBrand = () => {
    editModal.onClose();
    store.refetch?.();
  };

  const deleteModal = useDisclosure();
  const [del] = useDeleteBrandMutation({
    onCompleted(data) {
      store.deleteBrandById(data.deleteBrand.id);
    },
    ...onError(),
  });

  const popoverActions = useMemo(() => {
    const actions: ExpandableItemProps["popoverActions"] = [];

    can("brand_update") &&
      actions.push({
        content: t`Edit`,
        iconName: "edit-02",
        onAction: editModal.onOpen,
      });

    can("brand_delete") &&
      actions.push({
        content: t`Delete`,
        iconName: "trash-03",
        onAction: deleteModal.onOpen,
      });

    return actions;
  }, []);

  const pipedriveCustomFieldValues = useMemo(
    () =>
      brand.pipedriveCustomFieldValues.filter(
        (item) => !!item.customField.label && !!item.value,
      ),
    [brand.pipedriveCustomFieldValues],
  );

  return (
    <Card.Subsection>
      <ExpandableItem
        noExpand={!pipedriveCustomFieldValues.length}
        header={
          <HStack>
            <BrandLogo mr="1" src={brand.image?.urls.thumbnail} />
            <Link
              href={{ pathname: "/brands/detail", query: { id: brand.id } }}
            >
              {brand.name}
              {!!brand.archived_at && (
                <Badge ml="3" colorScheme="red">
                  <Trans>Archived</Trans>
                </Badge>
              )}
            </Link>
          </HStack>
        }
        popoverActions={popoverActions}
      >
        {pipedriveCustomFieldValues.map((item) => (
          <LabeledItem
            key={`${item.customField.label}-${item.value}`}
            label={item.customField.label ?? ""}
          >
            {item.value}
          </LabeledItem>
        ))}
      </ExpandableItem>

      <ModalBrand
        {...editModal}
        brand={brand}
        clientId={store.client.id}
        onSubmit={onEditBrand}
      />

      {deleteModal.isOpen && (
        <ModalConfirm
          onConfirm={() => del({ variables: { id: brand.id } })}
          {...deleteModal}
        />
      )}
    </Card.Subsection>
  );
});

export const CardBrands: FunctionComponent<React.PropsWithChildren<object>> =
  observer(function CardBrands() {
    const store = useContext(PageClientDetailCtx);
    const client = store.client;

    const addModal = useDisclosure();
    const onAddBrand = () => {
      addModal.onClose();
      store.refetch?.();
    };

    if (!client) return null;

    const { brands } = client;

    return (
      <React.Fragment>
        <Box w="full">
          <CardHeader
            icon="bank"
            title={
              <Heading size="md">
                <Trans>Brands</Trans>
              </Heading>
            }
            action={
              can("brand_create") && (
                <Button
                  colorScheme="grey"
                  leftIcon={<Icon name="plus" />}
                  onClick={addModal.onOpen}
                  variant="outline"
                >
                  <Trans>Add new brand</Trans>
                </Button>
              )
            }
          />
          <Card.Section>
            {brands.map((i) => (
              <SectionBrand key={i.id} brand={i} />
            ))}
          </Card.Section>
        </Box>

        <ModalBrand
          {...addModal}
          clientId={store.client.id}
          onSubmit={onAddBrand}
        />
      </React.Fragment>
    );
  });
